import { Editor } from '@tiptap/core'
import { SlashMenuConfig, SlashMenuItemType, SuggestionItem } from './types.js'
import i18next from 'i18next'
import {
  Facebook,
  Heading1,
  Heading2,
  Heading3,
  Image,
  Instagram,
  Link,
  List,
  ListOrdered,
  Minus,
  Music,
  Quote,
  Twitter,
  Video,
  Youtube,
} from 'lucide-react'
export const getDefaultSlashMenuItems = (editor: Editor): SuggestionItem[] => {
  return [
    {
      title: i18next.t('heading1') || 'Heading 1',
      subtext: i18next.t('heading1Desc') || 'High priority section title',
      aliases: ['h1'],
      badge: Heading1,
      group: 'format',
      onSelect: ({ editor }) => {
        editor.chain().focus().setHeading({ level: 1 }).run()
      },
    },
    {
      title: i18next.t('heading2') || 'Heading 2',
      subtext: i18next.t('heading2Desc') || 'Medium priority section title',
      aliases: ['h2'],
      badge: Heading2,
      group: 'format',
      onSelect: ({ editor }) => {
        editor.chain().focus().setHeading({ level: 2 }).run()
      },
    },
    {
      title: i18next.t('heading3') || 'Heading 3',
      subtext: i18next.t('heading3Desc') || 'Low priority section title',
      aliases: ['h3'],
      badge: Heading3,
      group: 'format',
      onSelect: ({ editor }) => {
        editor.chain().focus().setHeading({ level: 3 }).run()
      },
    },
    {
      title: i18next.t('bulletList') || 'Bullet List',
      subtext: i18next.t('bulletListDesc') || 'Unordered list of items',
      aliases: ['ul'],
      badge: List,
      group: 'format',
      onSelect: ({ editor }) => {
        editor.chain().focus().toggleBulletList().run()
      },
    },
    // Uncomment if you want a numbered list option
    // {
    //   title: i18next.t("numberedList") || 'Numbered List',
    //   subtext: i18next.t("numberedListDesc") || "Ordered list of items",
    //   aliases: ["ol"],
    //   badge: ListOrdered,
    //   group: "format",
    //   onSelect: ({ editor }) => {
    //     editor.chain().focus().toggleOrderedList().run();
    //   },
    // },
    {
      title: i18next.t('blockquote') || 'Blockquote',
      subtext: i18next.t('blockquoteDesc') || 'Element for quoting',
      aliases: [],
      badge: Quote,
      group: 'format',
      onSelect: ({ editor }) => {
        editor.chain().focus().setBlockquote().run()
      },
    },
    {
      title: i18next.t('image') || 'Image',
      subtext: i18next.t('imageDesc') || 'Insert an image',
      aliases: ['img'],
      badge: Image,
      group: 'insert',
      onSelect: ({ editor }) => {
        editor.chain().focus().setImageUpload('image').run()
      },
    },
    {
      title: i18next.t('video') || 'Video',
      subtext: i18next.t('videoDesc') || 'Insert a video',
      aliases: ['video'],
      badge: Video,
      group: 'insert',
      onSelect: ({ editor }) => {
        editor.chain().focus().setImageUpload('video').run()
      },
    },
    {
      title: i18next.t('horizontalRule') || 'HorizontalRule',
      subtext: i18next.t('horizontalRuleDesc') || 'Insert a horizontal divider',
      aliases: ['hr'],
      badge: Minus,
      group: 'insert',
      onSelect: ({ editor }) => {
        editor.chain().focus().setHorizontalRule().run()
      },
    },
    {
      title: i18next.t('embed') || 'Embed Link',
      subtext: i18next.t('iframeDesc') || 'Insert an Embed link',
      aliases: ['linkPreview'],
      badge: Link,
      group: 'embed',
      onSelect: ({ editor }) => {
        editor.chain().focus().setLinkPreview().run()
      },
    },
    {
      title: i18next.t('twitter') || 'Twitter',
      subtext: i18next.t('twitterDesc') || 'Insert a Twitter embed',
      aliases: ['x', 'twitter'],
      badge: Twitter,
      group: 'embed',
      onSelect: ({ editor }) => {
        editor.chain().focus().insertTwitter().run()
      },
    },
    {
      title: i18next.t('facebook') || 'Facebook',
      subtext: i18next.t('facebookDesc') || 'Insert a Facebook embed',
      aliases: ['meta', 'facebook'],
      badge: Facebook,
      group: 'embed',
      onSelect: ({ editor }) => {
        editor.chain().focus().insertFacebook().run()
      },
    },
    {
      title: i18next.t('instagram') || 'Instagram',
      subtext: i18next.t('instagramDesc') || 'Insert a Instagram embed',
      aliases: ['instagram'],
      badge: Instagram,
      group: 'embed',
      onSelect: ({ editor }) => {
        editor.chain().focus().insertInstagram().run()
      },
    },
    {
      title: i18next.t('youtube') || 'Youtube',
      subtext: i18next.t('youtubeDesc') || 'Insert a Youtube embed',
      aliases: ['youtube'],
      badge: Youtube,
      group: 'embed',
      onSelect: ({ editor }) => {
        editor.chain().focus().insertYoutube().run()
      },
    },
    {
      title: i18next.t('tiktok') || 'Tiktok',
      subtext: i18next.t('tiktokDesc') || 'Insert a Tiktok embed',
      aliases: ['tiktok'],
      badge: Music,
      group: 'embed',
      onSelect: ({ editor }) => {
        editor.chain().focus().insertTiktok().run()
      },
    },
  ]
}

export const filterSuggestionItems = (items: SuggestionItem[], query: string): SuggestionItem[] => {
  if (!query) return items

  const normalizedQuery = query.toLowerCase().trim()

  return items.filter((item) => {
    const titleMatch = item.title.toLowerCase().includes(normalizedQuery)
    const subtextMatch = item.subtext.toLowerCase().includes(normalizedQuery)
    const aliasesMatch = item.aliases.some((alias) => alias.toLowerCase().includes(normalizedQuery))

    return titleMatch || subtextMatch || aliasesMatch
  })
}

export const getSlashMenuItems = (editor: Editor, config: SlashMenuConfig): SuggestionItem[] => {
  const defaultItems = getDefaultSlashMenuItems(editor)

  // Filter by enabled items
  const enabledItems = defaultItems.filter((item) => {
    const itemType = item.title.toLowerCase().replace(/\s+/g, '_') as SlashMenuItemType
    return config.enabledItems.includes(itemType) || config.enabledItems.includes('text')
  })

  // Add custom items
  const customItems = config.customItems || []

  // Apply group assignments
  const itemsWithGroups = [...enabledItems, ...customItems].map((item) => ({
    ...item,
    group: config.itemGroups?.[item.title.toLowerCase().replace(/\s+/g, '_')] || item.group,
  }))

  return itemsWithGroups
}
